import { t } from "@lingui/macro";
import { useAtomValue } from "jotai";

import { getRoundFromIdAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getRoundFromIdAtom/getRoundFromIdAtom";
import { isAllMemberGroupAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/isAllMemberGroupAtom/isAllMemberGroupAtom";
import { isAlreadyPaidAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/isAlreadyPaidAtom/isAlreadyPaidAtom";
import {
  currentBookingAtom,
  currentModalStepAtom,
} from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/stores";
import { useFlags } from "@/utils/launchDarkly";
import { Button } from "@whoosh/design-system/src/components/controls";
import { KebabVerticalIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Menu } from "@whoosh/design-system/src/components/overlay/Menu";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { MovePlayersBetweenGroupsMenuItem } from "./menuItems/MovePlayersBetweenGroupsMenuItem/MovePlayersBetweenGroupsMenuItem";
import { RemoveRoundMenuItem } from "./menuItems/RemoveRoundMenuItem/RemoveRoundMenuItem";
import { useShouldShowRemoveRoundMenuItem } from "./menuItems/RemoveRoundMenuItem/useShouldShowRemoveRoundMenuItem";
import { SetNonPlayingHostMenuItem } from "./menuItems/SetNonPlayingHostMenuItem/SetNonPlayingHostMenuItem";
import { SetOwnerMenuItem } from "./menuItems/SetOwnerMenuItem/SetOwnerMenuItem";
import { useShouldShowSetOwnerMenuOption } from "./menuItems/SetOwnerMenuItem/useShouldShowSetOwnerMenuOption";
import { SwapPlayerMenuItem } from "./menuItems/SwapPlayerMenuItem/SwapPlayerMenuItem";
import { SwapPlayersBetweenGroupsMenuItem } from "./menuItems/SwapPlayersBetweenGroupsMenuItem/SwapPlayersBetweenGroupsMenuItem";
import { shouldShowNonPlayingHostMenuItem } from "./utils/shouldShowNonPlayingHostMenuItem";

type Props = {
  roundId: string;
};

export const RoundOverflowMenu = ({ roundId }: Props) => {
  const { memberNonPlayingHostToggle } = useFlags();
  const shouldShowSetOwnerMenuOption = useShouldShowSetOwnerMenuOption(roundId);
  const shouldShowRemoveRoundMenuItem =
    useShouldShowRemoveRoundMenuItem(roundId);

  const round = useAtomValue(getRoundFromIdAtom(roundId));
  const currentModalStep = useAtomValue(currentModalStepAtom);
  const isAllMemberGroup = useAtomValue(isAllMemberGroupAtom);
  const isPublicBooking =
    round?.player.clientType === "customer" ||
    round?.player.clientType === "unknownCustomer";
  const isAlreadyPaid = useAtomValue(isAlreadyPaidAtom);
  const currentBooking = useAtomValue(currentBookingAtom);
  const isAggregateRequest =
    currentBooking?.rounds.some(
      (candidate) => candidate.bookingIndex != null
    ) ?? false;

  const isMember =
    round?.player.clientType === "member" ||
    round?.player.clientType === "memberHold";

  const shouldShowNonPlayingHostOption = shouldShowNonPlayingHostMenuItem({
    memberNonPlayingHostToggle,
    isMember,
    isEditRoundsPrivateStep: currentModalStep.type === "EditRoundsPrivate",
    isAllMemberGroup,
  });
  /**
   * As long as we have at least 1 item -- show the menu. otherwise simply dont' render it
   */
  const shouldShowMenu =
    shouldShowSetOwnerMenuOption ||
    shouldShowRemoveRoundMenuItem ||
    isAggregateRequest ||
    (isPublicBooking && !isAlreadyPaid) ||
    shouldShowNonPlayingHostOption;

  return shouldShowMenu ? (
    <Menu
      openPosition="bottom-end"
      trigger={({ onPress }) => (
        <Button
          accessibilityLabel={t`Round Overflow Menu`}
          size="tiny"
          variant="secondary"
          onPress={onPress}
          leftIcon={<KebabVerticalIcon />}
          testID="round-overflow-menu-button"
        />
      )}
      content={({ closeMenu }) => (
        <Menu.Content>
          <VStack>
            {/* For owner we just disabled the overflow menu, until it is not clear what we want to do with it */}
            {shouldShowSetOwnerMenuOption && !round?.isOwner ? (
              <SetOwnerMenuItem roundId={roundId} closeMenu={closeMenu} />
            ) : null}
            {isAggregateRequest ? (
              <>
                <MovePlayersBetweenGroupsMenuItem
                  roundId={roundId}
                  closeMenu={closeMenu}
                />
                <SwapPlayersBetweenGroupsMenuItem
                  roundId={roundId}
                  closeMenu={closeMenu}
                />
              </>
            ) : !round?.isOwner ? (
              <SwapPlayerMenuItem roundId={roundId} closeMenu={closeMenu} />
            ) : null}
            {shouldShowNonPlayingHostOption ? (
              <SetNonPlayingHostMenuItem
                roundId={roundId}
                closeMenu={closeMenu}
              />
            ) : null}
            {shouldShowRemoveRoundMenuItem ? (
              <RemoveRoundMenuItem roundId={roundId} />
            ) : null}
          </VStack>
        </Menu.Content>
      )}
    />
  ) : null;
};
